import { Box } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { ApplicationStepContainer } from '@/components/modules/Application/components/_shared/components/ApplicationStepContainer';
import { ApplicationStepNav } from '@/components/modules/Application/components/_shared/components/ApplicationStepNav';
import { Section } from '@/components/modules/Application/components/_shared/components/Section/Section';
import { SectionTitle } from '@/components/modules/Application/components/_shared/components/Section/SectionTitle';
import { AutoDetailsFormWrapper } from '@/components/modules/Application/components/Cart/AutoCart/components/AutoDetailsFormWrapper';
import { LastVehiclesSelector } from '@/components/modules/Application/components/Cart/AutoCart/components/LastVehiclesSelector';
import { useAutoDetails } from '@/components/modules/Application/components/Cart/AutoCart/hooks/useAutoDetails';
import { AutoCartExtractorMap } from '@/components/modules/Application/components/Cart/AutoCart/maps/autoCartExtractor.map';
import { useAutomobileForm } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/hooks/useAutomobileForm';
import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import { useCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCart';
import { CartItemDialogFormFields } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/schemas/CartItemDialogFormSchema';
import { AiButton } from '@/components/shared/components/AiButton';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { DocumentExtractorProvider } from '@/components/shared/components/DocumentExtractorDialog/contexts/DocumentExtractorContext';
import { DocumentExtractorDialog } from '@/components/shared/components/DocumentExtractorDialog/DocumentExtractorDialog';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';

const AutoDetailsFormInner = () => {
  const formContext = useFormContext<CartItemDialogFormFields>();

  const { setValue, getValues, handleSubmit, formState } = formContext;

  const { onSubmit, paymentMethodsAvailable, cart } = useAutoDetails();
  const { setCartItem } = useCart();
  const { updateAutomobileData } = useAutomobileForm();
  const { getTextIdTranslation } = useTranslation();

  const [updatingAutomobileData, setUpdatingAutomobileData] = useState(false);
  const [extractorOpen, setExtractorOpen] = useState(false);

  const showExtractor = false; // ENV_IS_PROD !== 'true' || themeName === CustomTheme.QUADIS;

  //Setting initial cart
  useEffect(() => {
    if (cart && cart.items.length === 0) {
      const item = formState.defaultValues!.item as AutomobileFormItem;
      setCartItem(item, 0);
    }
  }, [cart]);

  const onUpdateAutomobileData = async (cartItem: AutomobileFormItem) => {
    setUpdatingAutomobileData(true);
    await updateAutomobileData(cartItem);
    setUpdatingAutomobileData(false);
  };

  return (
    <>
      <ApplicationStepContainer>
        <Section>
          <Box position={'absolute'} right={0} top={0} display="flex" alignItems="center" gridGap={8}>
            {showExtractor && (
              <AiButton
                size="small"
                startIcon={<Icon size={16}>auto_awesome</Icon>}
                onClick={() => setExtractorOpen(true)}
              >
                <Text tId="DOCUMENT_EXTRACTOR_BUTTON_TEXT" />
              </AiButton>
            )}
            <LastVehiclesSelector
              onUpdated={(cartItem) => setValue('item', cartItem, { shouldValidate: true })}
              updateAutomobileData={onUpdateAutomobileData}
            />
          </Box>
          {showExtractor && (
            <DocumentExtractorDialog
              opened={extractorOpen}
              setOpened={setExtractorOpen}
              mapValues={(results) => Promise.resolve(AutoCartExtractorMap.toFormValues(results))}
              onConfirmed={async () => {
                const currentItem = getValues('item') as AutomobileFormItem;
                await onUpdateAutomobileData(currentItem);
              }}
            />
          )}
          <SectionTitle title={'AUTO_DETAILS'} subtitle={'AUTO_DETAILS_DESCRIPTION'} />
          <CustomPaper fill="paper" style={{ padding: '32px 36px' }}>
            <AutoDetailsFormWrapper loading={updatingAutomobileData} />
          </CustomPaper>
        </Section>
        <ApplicationStepNav
          onNext={handleSubmit(() => onSubmit())}
          disableOnNext={!paymentMethodsAvailable}
          tooltipOnNextDisabled={
            !paymentMethodsAvailable
              ? getTextIdTranslation('NO_PAYMENT_METHODS_AVAILABLE_FOR_AMOUNT')
              : getTextIdTranslation('')
          }
        />
      </ApplicationStepContainer>
    </>
  );
};

export const AutoDetailsForm = () => (
  <DocumentExtractorProvider>
    <AutoDetailsFormInner />
  </DocumentExtractorProvider>
);
